import { type KeyboardEvent, useId, useLayoutEffect, useRef, useState } from "react";
import { Icon } from "../Icon";
import {
  addDays,
  addMonths,
  firstOfMonth,
  formatCalendarDate,
  formatMonth,
  lastOfMonth,
  monthOf,
  monthWeeks,
  weekdayOf,
} from "./availabilityCalendar";

/**
 * A day as the calendar shows it: times to pick, none, still loading, not loaded (the search
 * failed or stopped short of it), or outside the window.
 */
export type CalendarDayState = "open" | "none" | "loading" | "unknown" | "closed";

const weekdays = [
  ["Sunday", "S"],
  ["Monday", "M"],
  ["Tuesday", "T"],
  ["Wednesday", "W"],
  ["Thursday", "T"],
  ["Friday", "F"],
  ["Saturday", "S"],
] as const;

const stateWords: Record<CalendarDayState, string> = {
  open: "times available",
  none: "no times",
  loading: "loading times",
  unknown: "times not loaded",
  closed: "no times",
};

/**
 * One month of days as a grid of buttons (the WAI-ARIA date grid pattern): a day with times is
 * a round blue-wash button, the chosen day is filled, and every other day is quiet and
 * aria-disabled but still reachable, so arrow keys move through the whole month. One day is in
 * the tab order; arrows move a day or a week, Home and End go to the week's ends, Page Up and
 * Page Down change the month. Each day's name says whether it has times. The month buttons stay
 * focusable at the window's ends (aria-disabled), so a press there never drops the focus.
 */
export function MonthCalendar({
  month,
  onMonthChange,
  minMonth,
  maxMonth,
  today,
  selected,
  onSelect,
  dayState,
}: {
  /** The month on screen (YYYY-MM). */
  readonly month: string;
  readonly onMonthChange: (month: string) => void;
  /** The earliest and latest months the patient may page to. */
  readonly minMonth: string;
  readonly maxMonth: string;
  /** Today on the clinic's calendar, marked with a dot. */
  readonly today: string;
  /** The chosen day, or "" for none. */
  readonly selected: string;
  /** A day with times was chosen: `pointer` when tapped or clicked, not by the keyboard. */
  readonly onSelect: (date: string, pointer: boolean) => void;
  readonly dayState: (date: string) => CalendarDayState;
}) {
  const labelId = useId();
  const grid = useRef<HTMLTableElement>(null);
  const [focusDate, setFocusDate] = useState<string | null>(null);
  const moveFocus = useRef(false);

  const inMonth = (date: string) => monthOf(date) === month;
  // The one day in the tab order: where the keyboard last was, else the chosen day, today, the
  // first day with times, or the 1st.
  const weeks = monthWeeks(month);
  const days = weeks.flat().flatMap((cell) => (cell.date === null ? [] : [cell.date]));
  const tabDate =
    (focusDate !== null && inMonth(focusDate) ? focusDate : null) ??
    (inMonth(selected) ? selected : null) ??
    (inMonth(today) ? today : null) ??
    days.find((date) => dayState(date) === "open") ??
    firstOfMonth(month);

  // A layout effect, so the move lands in the commit that asked for it. A passive effect still
  // pending from the data load's commit could run first, consume the flag and focus the old day.
  useLayoutEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    grid.current?.querySelector<HTMLButtonElement>(`button[data-date="${tabDate}"]`)?.focus();
  });

  const first = firstOfMonth(minMonth);
  const last = lastOfMonth(maxMonth);
  const goTo = (date: string) => {
    const target = date < first ? first : date > last ? last : date;
    setFocusDate(target);
    moveFocus.current = true;
    if (!inMonth(target)) onMonthChange(monthOf(target));
  };
  const keyDown = (event: KeyboardEvent<HTMLButtonElement>, date: string) => {
    const moves: Record<string, () => string> = {
      ArrowLeft: () => addDays(date, -1),
      ArrowRight: () => addDays(date, 1),
      ArrowUp: () => addDays(date, -7),
      ArrowDown: () => addDays(date, 7),
      Home: () => addDays(date, -weekdayOf(date)),
      End: () => addDays(date, 6 - weekdayOf(date)),
      PageUp: () => sameDayIn(date, -1),
      PageDown: () => sameDayIn(date, 1),
    };
    const move = moves[event.key];
    if (move === undefined) return;
    event.preventDefault();
    goTo(move());
  };
  const atFirst = month <= minMonth;
  const atLast = month >= maxMonth;
  const page = (count: number) => {
    if (count < 0 ? atFirst : atLast) return;
    setFocusDate(null);
    onMonthChange(addMonths(month, count));
  };

  return (
    <div className="pa-calendar">
      <div className="pa-calendar-head">
        <button
          type="button"
          className="pa-calendar-nav"
          aria-label="Previous month"
          aria-disabled={atFirst ? true : undefined}
          onClick={() => page(-1)}
        >
          <Icon name="chevronLeft" />
        </button>
        <p className="pa-calendar-month" id={labelId} aria-live="polite">
          {formatMonth(month)}
        </p>
        <button
          type="button"
          className="pa-calendar-nav"
          aria-label="Next month"
          aria-disabled={atLast ? true : undefined}
          onClick={() => page(1)}
        >
          <Icon name="chevronRight" />
        </button>
      </div>
      <table
        className="pa-calendar-grid"
        // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: the WAI-ARIA date grid pattern; role=grid lets screen readers pass the arrow keys to the calendar.
        role="grid"
        aria-labelledby={labelId}
        ref={grid}
      >
        <thead>
          <tr>
            {weekdays.map(([name, letter]) => (
              <th key={name} scope="col" abbr={name}>
                {letter}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week.find((cell) => cell.date !== null)?.key ?? month}>
              {week.map(({ key, date }) => {
                if (date === null) return <td key={key} />;
                const state = dayState(date);
                const chosen = date === selected;
                const isToday = date === today;
                return (
                  <td key={key}>
                    <button
                      type="button"
                      data-date={date}
                      className={`pa-calendar-day is-${state}${chosen ? " is-selected" : ""}${isToday ? " is-today" : ""}`}
                      tabIndex={date === tabDate ? 0 : -1}
                      aria-label={`${formatCalendarDate(date)}${isToday ? ", today" : ""}, ${stateWords[state]}`}
                      aria-pressed={state === "open" || chosen ? chosen : undefined}
                      aria-disabled={state === "open" ? undefined : true}
                      aria-current={isToday ? "date" : undefined}
                      onClick={(event) => {
                        setFocusDate(date);
                        // A keyboard press is a click with no pointer count (detail 0).
                        if (state === "open") onSelect(date, event.detail > 0);
                      }}
                      onKeyDown={(event) => keyDown(event, date)}
                    >
                      {Number(date.slice(8))}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The same day of the month `count` months away, or that month's last day. */
function sameDayIn(date: string, count: number): string {
  const month = addMonths(monthOf(date), count);
  const last = lastOfMonth(month);
  const day = `${month}-${date.slice(8)}`;
  return day > last ? last : day;
}
